import { getScopedClient } from './supabase';

export interface WebinarListItem {
  id: string;
  title: string | null;
  subtitle: string | null;
  status: string;
  launch_status: string | null;
  error_message: string | null;
  is_archived: boolean;
  share_token: string | null;
  is_evergreen: boolean;
  evergreen_slug: string | null;
  created_at: string;
}

export async function getWebinars(options?: {
  includeArchived?: boolean;
  sortAsc?: boolean;
  dateFilter?: '7d' | '30d' | '90d' | 'all';
}): Promise<WebinarListItem[]> {
  const client = getScopedClient();

  let query = client
    .from('webinars')
    .select('id, title, subtitle, status, launch_status, error_message, is_archived, share_token, is_evergreen, evergreen_slug, created_at')
    .order('created_at', { ascending: options?.sortAsc ?? false });

  if (!options?.includeArchived) {
    query = query.eq('is_archived', false);
  }

  if (options?.dateFilter && options.dateFilter !== 'all') {
    const now = new Date();
    const daysMap = { '7d': 7, '30d': 30, '90d': 90 };
    const days = daysMap[options.dateFilter];
    const cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    query = query.gte('created_at', cutoff.toISOString());
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(`Failed to fetch webinars: ${error.message}`);
  }

  return (data as WebinarListItem[]) || [];
}

export async function deleteWebinar(id: string): Promise<void> {
  const client = getScopedClient();
  const { error } = await client.from('webinars').delete().eq('id', id);
  if (error) {
    throw new Error(`Failed to delete webinar: ${error.message}`);
  }
}

export async function archiveWebinar(id: string): Promise<void> {
  const client = getScopedClient();
  const { error } = await client
    .from('webinars')
    .update({ is_archived: true })
    .eq('id', id);
  if (error) {
    throw new Error(`Failed to archive webinar: ${error.message}`);
  }
}

export async function unarchiveWebinar(id: string): Promise<void> {
  const client = getScopedClient();
  const { error } = await client
    .from('webinars')
    .update({ is_archived: false })
    .eq('id', id);
  if (error) {
    throw new Error(`Failed to unarchive webinar: ${error.message}`);
  }
}

export async function shareWebinar(id: string): Promise<string> {
  const token = crypto.randomUUID();
  const { error } = await getScopedClient()
    .from('webinars')
    .update({ share_token: token })
    .eq('id', id);
  if (error) {
    throw new Error(`Failed to share webinar: ${error.message}`);
  }
  return token;
}

export async function revokeShare(id: string): Promise<void> {
  const { error } = await getScopedClient()
    .from('webinars')
    .update({ share_token: null })
    .eq('id', id);
  if (error) {
    throw new Error(`Failed to revoke share: ${error.message}`);
  }
}

export async function makeEvergreen(id: string): Promise<string> {
  const slug = crypto.randomUUID().substring(0, 12);
  const { error } = await getScopedClient()
    .from('webinars')
    .update({ is_evergreen: true, evergreen_slug: slug })
    .eq('id', id);
  if (error) {
    throw new Error(`Failed to make evergreen: ${error.message}`);
  }
  return slug;
}

export async function removeEvergreen(id: string): Promise<void> {
  const { error } = await getScopedClient()
    .from('webinars')
    .update({ is_evergreen: false, evergreen_slug: null })
    .eq('id', id);
  if (error) {
    throw new Error(`Failed to remove evergreen: ${error.message}`);
  }
}
